import React, { useEffect, useState } from 'react';

import { faBolt, faClipboard, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faBolt as faBoltRegular, faCirclePlus, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActivityList from './activity_list';
import ActivityModal from 'bundles/apex/activities/modal';
import LogActivityModal from 'bundles/apex/activities/log_activity/modal';
import SendEmailModal from 'bundles/apex/activities/send_email/modal';
import ReauthModal from 'bundles/apex/activities/send_email/reauth_modal';
import { shouldAutoOpenCompose } from 'bundles/apex/activities/send_email/helpers';
import { Button } from 'components/ui/button';
import CopyOverlay from 'components/copy_overlay';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Separator } from '@buildoutinc/blueprint-react/ui/Separator';
import Filters from 'bundles/apex/activities/filters';
import LoadingSpinner from 'components/ui/loading_spinner';
import { buildActivity, pinActivity } from 'bundles/apex/activities/form/helpers';
import { useActivitiesState } from 'bundles/apex/activities/hooks';
import * as filterHelpers from './filters/helpers';
import Pagination from 'components/ui/pagination';
import classNames from 'classnames';

import type { Activity, Features, FilterOptions, PaginationData } from 'bundles/apex/activities/types';
import type { SendEmailConfig } from 'bundles/apex/activities/send_email/types';
import type { ActivityFilters } from './filters/types';
import type { MenuOptions } from 'bundles/apex/activities/types';
import type { Parent, ParentOption } from 'bundles/crm/types';
import type { Paths } from 'bundles/apex/activities/hooks';
import { showSnackbar } from 'components/ui/snackbar/helper';

export interface ActivitiesProps {
  activities: Activity[];
  activityMenuOptions?: MenuOptions;
  importerProps: any;
  isBackofficeAdmin?: boolean;
  forUserId?: number;
  currentParent?: Parent | Parent[];
  features?: Features;
  options?: {
    parents?: ParentOption[];
  }
  filters?: boolean | ActivityFilters;
  filterOptions?: FilterOptions;
  header?: boolean;
  hideAfterReparenting?: boolean;
  showRedesignModal?: boolean;
  subjectName?: string;
  // Render the header actions (send email, add activity) without the timeline itself.
  // For viewers who may contribute to a record but not read its history.
  hideList?: boolean;
  inboxAddress?: string;
  listClassName?: string;
  paginate?: boolean;
  paths?: { availableTags?: string, create?: string, latest?: string } & Paths,
  seedFiltersFromUrl?: boolean;
  sendEmail?: SendEmailConfig;
  showParents?: boolean | 'path';
  showPinned?: boolean;
  smallCreateButton?: boolean;
  tagText?: string;
  onChange?: (activities: Activity[]) => void;
  onEmptyStateClick?: () => void;
  onPageDataChange?: (pageData: PaginationData) => void;
}

export default function Activities({
  activities: activitiesProp,
  activityMenuOptions,
  currentParent,
  features,
  options,
  filters: showFilters,
  filterOptions,
  header,
  hideAfterReparenting,
  hideList,
  inboxAddress,
  listClassName,
  showRedesignModal,
  paginate,
  paths,
  seedFiltersFromUrl,
  sendEmail,
  showParents,
  showPinned,
  smallCreateButton,
  subjectName,
  forUserId,
  isBackofficeAdmin,
  tagText,
  onChange,
  onEmptyStateClick,
  onPageDataChange,
}: ActivitiesProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);

  // Returning from the reauth OAuth flow lands here with ?compose_email=1; reopen the compose
  // modal for the contact the broker started from, then strip the flag so a refresh is a no-op.
  useEffect(() => {
    if (!shouldAutoOpenCompose(window.location.search, sendEmail)) return;

    setShowSendEmailModal(true);

    const params = new URLSearchParams(window.location.search);
    params.delete('compose_email');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
  }, []);
  const {
    activities,
    pinnedActivities,
    loading,
    pageData,
    addActivity,
    removeActivity,
    replaceActivity,
    changePinned,
    filters,
    changeFilters,
  } = useActivitiesState({
    activities: activitiesProp, paginate, paths, showPinned, seedFiltersFromUrl, onChange, onPageDataChange,
  });
  const hasFilters = filterHelpers.hasFilters(filters);

  const handleTogglePin = async (activity: Activity) => {
    const pinned = !activity.pinned;

    try {
      const saved: Activity = await pinActivity(activity, pinned);
      changePinned(saved, pinned);
    } catch {
      showSnackbar({ text: 'Something went wrong. ', variant: 'danger' });
    }
  };

  const activityListProps = {
    activityMenuOptions,
    currentParent,
    forUserId,
    hasFilters,
    hideAfterReparenting,
    isBackofficeAdmin,
    options,
    showParents,
    showPinned,
    showRedesignModal,
    subjectName,
    tagText,
    onDelete: removeActivity,
    onSave: replaceActivity,
    onTogglePin: handleTogglePin,
  };

  const pinnedDivider = (label: string) => {
    return (
      <div className="d-flex align-items-center gap-2 mb-2">
        <Separator className="flex-grow-1" />
        <span className="text-nowrap small text-uppercase text-muted fw-semibold">{label}</span>
        <Separator className="flex-grow-1" />
      </div>
    );
  };

  let onClick;
  if (onEmptyStateClick) {
    onClick = onEmptyStateClick;
  } else if (paths?.create) {
    onClick = () => setShowCreateModal(true);
  }

  return (
    <>
      {(activities.length === 0 && pinnedActivities.length === 0 && !loading && (!showFilters) && !hideList) ? (
        <Empty className="bg-white">
          <Empty.Media>
            <FontAwesomeIcon icon={faBolt} />
          </Empty.Media>
          <Empty.Title>No Activities to Display</Empty.Title>
          <Empty.Content>Get started by adding an activity</Empty.Content>
          {onClick && (
            <Empty.Actions>
              <Button icon={faPlus} label="Add activity" onClick={onClick} />
            </Empty.Actions>
          )}
        </Empty>
      ) : (
        <div className="d-flex flex-column flex-grow-1 overflow-hidden gap-3">
          {header && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon className="me-2 text-primary" icon={faBoltRegular} size="lg" />
                <h6 className="fw-bold" key="header">Activities</h6>
                {inboxAddress && (
                  <CopyOverlay
                    className="text-muted"
                    dark
                    text={inboxAddress}
                    tooltip="Copy inbox address for this record"
                  >
                    <FontAwesomeIcon icon={faClipboard} />
                  </CopyOverlay>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                {sendEmail?.enabled && sendEmail.path && (
                  <Button
                    icon={faEnvelope}
                    key="send-email"
                    variant="outline"
                    onClick={() => (sendEmail.canSend ? setShowSendEmailModal(true) : setShowReauthModal(true))}
                  >
                    Send Email
                  </Button>
                )}
                {paths?.create && (
                  smallCreateButton ? (
                    <Button
                      icon={faCirclePlus}
                      key="add"
                      label="Add Activity"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCreateModal(true)}
                    />
                  ) : (
                    <Button icon={faPlus} key="add" onClick={() => setShowCreateModal(true)}>
                      New activity
                    </Button>
                  ))}
              </div>
            </div>
          )}

          <Filters
            features={features}
            filterOptions={filterOptions}
            filters={filters}
            show={showFilters}
            onChange={changeFilters}
          />

          {!hideList && (loading ? (
            <LoadingSpinner />
          ) : (
            <div className={classNames(listClassName)}>
              {showPinned && pinnedActivities.length > 0 && (
                <div className="d-flex flex-column mb-3">
                  {pinnedDivider('Pinned')}
                  <ActivityList activities={pinnedActivities} {...activityListProps} />
                </div>
              )}
              {showPinned && pinnedActivities.length > 0 && activities.length > 0 && (
                pinnedDivider('Earlier this year')
              )}
              {!(showPinned && activities.length === 0 && pinnedActivities.length > 0) && (
                <ActivityList activities={activities} {...activityListProps} />
              )}
            </div>
          ))}

          {(!hideList && !loading && pageData) &&
            <Pagination
              currentPage={pageData.currentPage}
              totalPages={pageData.totalPages}
              onChange={page => changeFilters({ page })}
            />
          }
        </div>
      )}

      {showRedesignModal && paths?.create ? (
        <LogActivityModal
          fromEmail={sendEmail?.fromEmail}
          path={paths.create}
          sendEmail={sendEmail}
          show={showCreateModal}
          subjectName={subjectName}
          onHide={() => setShowCreateModal(false)}
          onLogged={(activity) => {
            addActivity(activity);
            setShowCreateModal(false);
          }}
        />
      ) : (
        <ActivityModal
          activity={buildActivity(paths?.create, { availableTagsPath: paths?.availableTags })}
          header="Add activity"
          options={options}
          show={showCreateModal}
          tagText={tagText}
          onHide={() => setShowCreateModal(false)}
          onSave={(activity) => {
            addActivity(activity);
            setShowCreateModal(false);
          }}
        />
      )}

      {showSendEmailModal && sendEmail?.path && (
        <SendEmailModal
          path={sendEmail.path}
          to={sendEmail.to}
          onClose={() => setShowSendEmailModal(false)}
        />
      )}

      {showReauthModal && sendEmail?.contactId && (
        <ReauthModal
          authPath={sendEmail.authPath}
          connected={sendEmail.connected}
          contactId={sendEmail.contactId}
          providerName={sendEmail.providerName}
          onClose={() => setShowReauthModal(false)}
        />
      )}
    </>
  );
}
